import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminApprovalPage = () => {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  
  const [pendingQuestions, setPendingQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [feedback, setFeedback] = useState('');

  useEffect(() => {
    if (user && user.role === 'admin') {
      fetchPendingQuestions();
    }
  }, [user]);

  const fetchPendingQuestions = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/orchestrator-v3/review/pending`, {
        withCredentials: true
      });
      
      // New orchestrator format
      if (response.data.status === 'success') {
        setPendingQuestions(response.data.data.pending_reviews || []);
      } else {
        setPendingQuestions([]);
      }
    } catch (error) {
      console.error('Error fetching pending questions:', error);
      setPendingQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (pairId) => {
    try {
      await axios.post(
        `${API}/orchestrator-v3/review/decide`,
        {
          pair_id: pairId,
          decision: 'approve',
          review_notes: ['Admin approved via UI']
        },
        { withCredentials: true }
      );
      
      alert(language === 'tr' ? '✅ Soru onaylandı!' : '✅ Question approved!');
      fetchPendingQuestions();
    } catch (error) {
      console.error('Approval error:', error);
      const errorMsg = error.response?.data?.hint_TR || 
        (language === 'tr' ? '❌ Onaylama hatası!' : '❌ Approval failed!');
      alert(errorMsg);
    }
  };

  const handleReject = async (pairId) => {
    if (!feedback.trim()) {
      alert(language === 'tr' ? 'Lütfen geri bildirim girin.' : 'Please provide feedback.');
      return;
    }
    
    try {
      await axios.post(
        `${API}/orchestrator-v3/review/decide`,
        {
          pair_id: pairId,
          decision: 'reject',
          review_notes: [feedback]
        },
        { withCredentials: true }
      );
      
      alert(language === 'tr' ? '❌ Soru reddedildi.' : '❌ Question rejected.');
      setSelectedQuestion(null);
      setFeedback('');
      fetchPendingQuestions();
    } catch (error) {
      console.error('Rejection error:', error);
      alert(language === 'tr' ? '❌ Reddetme hatası!' : '❌ Rejection failed!');
    }
  };

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <p className="text-xl text-gray-600">
            {language === 'tr' ? 'Admin yetkisi gereklidir.' : 'Admin access required.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Admin - Soru Onayı' : 'Admin - Question Approval'} />

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">
              🔍 {language === 'tr' ? 'Onay Bekleyen Sorular' : 'Pending Questions'}
            </h2>
            <span className="bg-blue-100 text-blue-800 px-4 py-2 rounded-full font-semibold">
              {pendingQuestions.length} {language === 'tr' ? 'soru' : 'questions'}
            </span>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">{language === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </div>
          ) : pendingQuestions.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {language === 'tr' ? 'Onay bekleyen soru yok.' : 'No pending questions.'}
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {pendingQuestions.map((pair) => (
                <div 
                  key={pair.pair_id || Math.random()} 
                  className="border-2 border-gray-300 rounded-xl p-6 hover:shadow-lg transition bg-white"
                >
                  {/* Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">
                        📚 {pair.concept_summary || 'Soru Çifti'}
                      </h3>
                      <div className="flex gap-2 items-center mb-2">
                        <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-semibold">
                          ⏳ Onay Bekliyor
                        </span>
                        {pair.TYT_item?.subject && (
                          <span className="bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm">
                            {pair.TYT_item.subject}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600">
                        {language === 'tr' ? 'Gönderen:' : 'Submitted by:'} {pair.teacher_id || 'N/A'}
                      </p>
                      <p className="text-xs text-gray-500">
                        {pair.submitted_at && new Date(pair.submitted_at).toLocaleString('tr-TR')}
                      </p>
                    </div>
                  </div>

                  {/* TYT Question */}
                  {pair.TYT_item && (
                    <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs font-bold text-blue-700 mb-2">🇹🇷 TYT (MCQ)</p>
                      <p className="text-gray-800 font-medium mb-2">
                        {pair.TYT_item.stem_TR}
                      </p>
                      
                      {pair.TYT_item.options && pair.TYT_item.options.length > 0 && (
                        <div className="space-y-1 text-sm text-gray-700 mt-2">
                          {pair.TYT_item.options.map((opt, i) => (
                            <div 
                              key={i} 
                              className={opt.startsWith(pair.TYT_item.correct_option) ? 'font-bold text-green-700' : ''}
                            >
                              {opt}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* AP Question */}
                  {pair.AP_item && (
                    <div className="mb-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <p className="text-xs font-bold text-purple-700 mb-2">🌍 AP (FRQ)</p>
                      <p className="text-gray-800 font-medium mb-2">
                        {pair.AP_item.stem_EN}
                      </p>
                      
                      {pair.AP_item.rubric_points && pair.AP_item.rubric_points.length > 0 && (
                        <div className="mt-2">
                          <p className="text-xs text-purple-700 font-semibold mb-1">Rubric:</p>
                          <ul className="list-disc list-inside text-sm text-gray-700">
                            {pair.AP_item.rubric_points.map((point, i) => (
                              <li key={i}>{point}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bridge Comment */}
                  {pair.bridge_comment && (
                    <div className="mb-4 p-3 bg-gray-50 rounded border border-gray-200">
                      <p className="text-xs text-gray-600 font-semibold mb-1">🔗 Köprü Yorumu:</p>
                      <p className="text-sm text-gray-700">{pair.bridge_comment}</p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 mt-4">
                    <button
                      onClick={() => handleApprove(pair.pair_id)}
                      className="flex-1 bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition font-semibold shadow-md hover:shadow-lg"
                    >
                      ✓ {language === 'tr' ? 'Onayla' : 'Approve'}
                    </button>
                    <button
                      onClick={() => setSelectedQuestion(pair)}
                      className="flex-1 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition font-semibold shadow-md hover:shadow-lg"
                    >
                      ✕ {language === 'tr' ? 'Reddet' : 'Reject'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Rejection Modal */}
      {selectedQuestion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              {language === 'tr' ? 'Soruyu Reddet' : 'Reject Question'}
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              {language === 'tr' 
                ? 'Lütfen reddedilme sebebini belirtin:' 
                : 'Please provide a reason for rejection:'}
            </p>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              rows={4}
              placeholder={language === 'tr' ? 'Geri bildirim...' : 'Feedback...'}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setSelectedQuestion(null);
                  setFeedback('');
                }}
                className="flex-1 bg-gray-300 text-gray-800 px-4 py-2 rounded-lg hover:bg-gray-400 transition"
              >
                {language === 'tr' ? 'İptal' : 'Cancel'}
              </button>
              <button
                onClick={() => handleReject(selectedQuestion.pair_id)}
                className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition"
              >
                {language === 'tr' ? 'Reddet' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminApprovalPage;
